import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { ResumeCitizensAttendancesIndicatorB1Data } from "@/services/esus/indicator/esb/b1/getResumeCitizensAttendancesIndicatorB1";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorEsb } from "@/utils/indicatorsEsb";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB1AttendanceTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	microarea: Column;
	citizenEsfHealthTeam: Column;
	attendanceEsbHealthTeam: Column;
	attendanceEsfHealthTeam: Column;
	lastDentalAttendance: Column;
};

type IndicatorEsbB1AttendancesTableProps = {
	citizensWithAttendances: ResumeCitizensAttendancesIndicatorB1Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenId: number, ine?: string) => void;
};

export function IndicatorEsbB1AttendancesTable({
	citizensWithAttendances,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsbB1AttendancesTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB1AttendanceTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		microarea: { title: "MA", visible: true },
		citizenEsfHealthTeam: { title: "Equipe ESF Cidadão", visible: true },
		attendanceEsbHealthTeam: { title: "Equipe ESB Atendimento", visible: true },
		attendanceEsfHealthTeam: { title: "Equipe ESF vinculada a ESB Atendimento", visible: true },
		lastDentalAttendance: { title: "Última Consulta", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.citizenEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.citizenEsfHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsbHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsbHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsfHealthTeam.title}
					</th>
					<th className={`px-2 ${!columns.lastDentalAttendance.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastDentalAttendance.title}
							<CardOverlayTrigger
								header="Última Consulta"
								message="Última Consulta realizada nos últimos 12 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB1AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB1AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB1AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithAttendances.length > 0
					? citizensWithAttendances.map((citizenWithAttendances, index) => {
							return (
								<tr key={`citizen-attendances-table-row-${index + 1}`}>
									<td
										className={`px-2 ${
											!columns.citizenName.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithAttendances.fullName}</div>
											<div className="text-muted">
												{citizenWithAttendances.birthdate
													? dayjs(
															citizenWithAttendances.birthdate
													  ).format("DD/MM/YYYY")
													: ""}
											</div>
										</div>
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.citizenAge.visible && "d-none"
										}`}
									>
										{citizenWithAttendances.birthdate
											? getAge(citizenWithAttendances.birthdate)
											: ""}
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.document.visible && "d-none"
										}`}
									>
										{citizenWithAttendances.cpf
											? formatIdentifier(citizenWithAttendances.cpf)
											: citizenWithAttendances.cns ?? ""}
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.microarea.visible && "d-none"
										}`}
									>
										{citizenWithAttendances.area ?? "FA"}
									</td>
									<td
										className={`px-2 ${
											!columns.citizenEsfHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithAttendances.healthTeamName}</div>
											<div className="text-muted">
												{citizenWithAttendances.healthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`px-2 ${
											!columns.attendanceEsbHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithAttendances.esbHealthTeamName}</div>
											<div className="text-muted">
												{citizenWithAttendances.esbHealthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`px-2 ${
											!columns.attendanceEsfHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithAttendances.linkedHealthTeamName}</div>
											<div className="text-muted">
												{citizenWithAttendances.linkedHealthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`text-center px-2 ${
											!columns.lastDentalAttendance.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>
												{getCitizenScoreStatusDescriptionIndicatorEsb(
													citizenWithAttendances.lastDentalAttendanceStatus
												)}
											</div>
											<div className="text-muted">
												{citizenWithAttendances.treatmentStartDate
													? dayjs(
															citizenWithAttendances.treatmentStartDate
													  ).format("DD/MM/YYYY")
													: ""}
											</div>
										</div>
									</td>
									<td className="text-center">
										<Button
											className="bg-primary py-1 px-3"
											onClick={() =>
												openCitizenHistory(
													citizenWithAttendances.id,
													citizenWithAttendances.esbHealthTeamIne
												)
											}
											title="Histórico do cidadão"
										>
											<FaEye className="fs-4" />
										</Button>
									</td>
								</tr>
							);
					  })
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) => count + (column?.visible ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
